import React, { useEffect, useState } from "react";
import { listPlatformPlans, updatePlatformPlan } from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm";

// Plans are edited locally then saved one card at a time. price is shown in
// major units (e.g. 12.00) and converted back to minor units (1200) on save.
const PlanEditorCard = ({ plan, onSaved }) => {
  const [draft, setDraft] = useState({
    name: plan.name,
    tagline: plan.tagline,
    price: (plan.price_minor_units / 100).toFixed(2),
    comparePrice: ((plan.compare_at_price_minor_units || 0) / 100).toFixed(2),
    ctaType: plan.cta_type || "subscribe",
    featuresText: (plan.features || []).join("\n"),
    highlight: plan.highlight,
    is_active: plan.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setStatus("");
    try {
      const priceMajor = parseFloat(draft.price);
      const compareMajor = parseFloat(draft.comparePrice);
      const updated = await updatePlatformPlan(plan.id, {
        name: draft.name,
        tagline: draft.tagline,
        price_minor_units: Number.isFinite(priceMajor) ? Math.round(priceMajor * 100) : 0,
        compare_at_price_minor_units: Number.isFinite(compareMajor) ? Math.round(compareMajor * 100) : 0,
        cta_type: draft.ctaType,
        features: draft.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
        highlight: draft.highlight,
        is_active: draft.is_active,
      });
      setStatus("Saved");
      onSaved?.(updated);
    } catch {
      setStatus("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-500">
          {plan.tier_key}
        </span>
        <span className="text-xs text-gray-500">{plan.currency} / {plan.billing_interval}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Name</span>
          <input className={inputCls} value={draft.name} onChange={(e) => set("name", e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Price ({plan.currency})</span>
          <input className={inputCls} value={draft.price} onChange={(e) => set("price", e.target.value)} inputMode="decimal" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Compare-at ({plan.currency})</span>
          <input className={inputCls} value={draft.comparePrice} onChange={(e) => set("comparePrice", e.target.value)} inputMode="decimal" placeholder="0 = no strikethrough" />
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Call to action</span>
          <select className={inputCls} value={draft.ctaType} onChange={(e) => set("ctaType", e.target.value)}>
            <option value="subscribe">Subscribe / switch tier</option>
            <option value="contact_sales">Talk to sales</option>
          </select>
        </label>
      </div>

      <label className="block mt-4">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Tagline</span>
        <input className={inputCls} value={draft.tagline} onChange={(e) => set("tagline", e.target.value)} />
      </label>

      <label className="block mt-4">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1 block">Features (one per line)</span>
        <textarea
          rows={6}
          className={`${inputCls} font-mono text-xs`}
          value={draft.featuresText}
          onChange={(e) => set("featuresText", e.target.value)}
        />
      </label>

      <div className="flex items-center gap-5 mt-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.highlight} onChange={(e) => set("highlight", e.target.checked)} className="w-4 h-4 accent-indigo-600" />
          Highlight (most popular)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.is_active} onChange={(e) => set("is_active", e.target.checked)} className="w-4 h-4 accent-indigo-600" />
          Active
        </label>
      </div>

      <div className="flex items-center gap-3 mt-5">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {status && (
          <span className={`text-sm ${status === "Saved" ? "text-emerald-500" : "text-red-500"}`}>{status}</span>
        )}
      </div>
    </Card>
  );
};

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setPlans(await listPlatformPlans());
      } catch {
        setError("Couldn't load plans.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSaved = (updated) =>
    setPlans((list) => list.map((p) => (p.id === updated.id ? updated : p)));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Plans</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Edit the public pricing catalog. Changes appear on the /pricing page immediately.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-500/10 text-red-500 text-sm">{error}</div>
      )}

      {loading ? (
        <Card>Loading…</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {plans.map((p) => (
            <PlanEditorCard key={p.id} plan={p} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPlans;
